import React from 'react';
import { AlertCircle, Check, CheckCircle2, Download, Eye, FileCode2, Mail, Plus, Search } from 'lucide-react';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { AppSelect } from '../../../components/AppSelect';
import { CreateOrganizationModal } from '../../contacts/CreateOrganizationModal';
import { fetchDispatchAccount } from '../../../utils/mailDispatchApi';
import type { AusschreibungDetail } from '../types';
import { TemplateTextField } from './TemplateTextField';
import { TenderGaebPreviewModal } from './TenderGaebPreviewModal';

const inputClass =
  'w-full px-3 py-2 bg-app-surface border border-app-border rounded-lg focus:ring-1 focus:ring-pipedrive-blue outline-none transition-all text-sm text-app-text';

const PLACEHOLDERS: Array<{ key: string; label: string }> = [
  { key: 'anrede', label: 'Anrede' },
  { key: 'lieferant', label: 'Lieferant' },
  { key: 'ansprechpartner', label: 'Ansprechpartner' },
  { key: 'bauvorhaben', label: 'Bauvorhaben' },
  { key: 'ausschreibung_nr', label: 'Ausschreibungs-Nr.' },
  { key: 'abgabefrist', label: 'Abgabefrist' },
  { key: 'ort', label: 'Ort' },
  { key: 'firma', label: 'Firma' },
];
const PLACEHOLDER_LABELS: Record<string, string> = Object.fromEntries(PLACEHOLDERS.map((p) => [p.key, p.label]));

/** Ansprechpartner-Auswahl: „andere Adresse“ statt eines Kontakts. */
const CUSTOM_EMAIL = '__custom';

type SupplierOrg = {
  id: string;
  name: string;
  primary_contact: { id: string; name: string | null; email: string | null } | null;
};

type OrgContact = { id: string; name: string | null; email: string | null; is_primary?: boolean };

type Recipient = {
  organizationId: string;
  supplierName: string;
  contacts: OrgContact[] | null;
  contactId: string | null;
  name: string;
  email: string;
};

type Mailbox = { id: string; email: string };

type AttachmentMeta = { filename: string; source: 'original' | 'generated'; size: number };

type SendResult = {
  email: string;
  supplier_name: string;
  status: 'sent' | 'already_sent' | 'in_progress' | 'failed';
  error?: string;
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function defaultSubject(): string {
  return 'Preisanfrage {{ausschreibung_nr}}: {{bauvorhaben}}';
}

function defaultBody(a: AusschreibungDetail): string {
  const lines = [
    '{{anrede}},',
    '',
    `für das Bauvorhaben „{{bauvorhaben}}“${a.customer_city?.trim() ? ' in {{ort}}' : ''} bitten wir um Ihr unverbindliches Angebot zu den Positionen im beigefügten Leistungsverzeichnis (GAEB X83).`,
    '',
    a.deadline
      ? 'Bitte senden Sie uns Ihr Angebot bis zum {{abgabefrist}}, gern als GAEB X84 oder als PDF mit Artikelnummern.'
      : 'Bitte senden Sie uns Ihr Angebot gern als GAEB X84 oder als PDF mit Artikelnummern.',
    '',
    'Vielen Dank und freundliche Grüße',
    '{{firma}}',
  ];
  return lines.join('\n');
}

/** Vorschau im Browser mit denselben Platzhaltern wie der Server (supplierRequestsCore.ts). */
function previewText(template: string, a: AusschreibungDetail, r: Recipient | null): string {
  const deadline = /^(\d{4})-(\d{2})-(\d{2})/.exec(a.deadline ?? '');
  const contact = r?.name.trim() ?? '';
  const vars: Record<string, string> = {
    anrede: contact ? `Guten Tag ${contact}` : 'Sehr geehrte Damen und Herren',
    lieferant: r?.supplierName ?? '',
    ansprechpartner: contact,
    bauvorhaben: a.title?.trim() ?? '',
    ausschreibung_nr: a.display_id?.trim() ?? '',
    abgabefrist: deadline ? `${deadline[3]}.${deadline[2]}.${deadline[1]}` : '',
    ort: a.customer_city?.trim() ?? '',
    firma: '[Ihre Firma]',
  };
  return template.replace(/\{\{\s*([a-z_]+)\s*\}\}/gi, (_m, key: string) => vars[key.toLowerCase()] ?? '');
}

interface Props {
  ausschreibung: AusschreibungDetail;
  canEdit: boolean;
  /** Schritt erledigt (gesendet oder übersprungen): weiter zur Akte. */
  onDone: (state: 'sent' | 'skipped') => void;
}

/**
 * Schritt „Großhändler anfragen“: Lieferanten aus den Kontakten wählen, Muster-Mail anpassen,
 * die X83 des LV an jeden Empfänger einzeln schicken, oder überspringen.
 */
export const TenderSupplierRequestStep: React.FC<Props> = ({ ausschreibung, canEdit, onDone }) => {
  const [query, setQuery] = React.useState('');
  const [orgs, setOrgs] = React.useState<SupplierOrg[]>([]);
  const [orgsLoading, setOrgsLoading] = React.useState(true);
  const [orgsError, setOrgsError] = React.useState<string | null>(null);
  const [recipients, setRecipients] = React.useState<Recipient[]>([]);
  const [mailboxes, setMailboxes] = React.useState<Mailbox[]>([]);
  const [accountId, setAccountId] = React.useState<string>('');
  const [subject, setSubject] = React.useState(defaultSubject);
  const [body, setBody] = React.useState(() => defaultBody(ausschreibung));
  const [showPreview, setShowPreview] = React.useState(false);
  const [attachment, setAttachment] = React.useState<AttachmentMeta | null>(null);
  const [attachmentError, setAttachmentError] = React.useState<string | null>(null);
  const [sending, setSending] = React.useState(false);
  const [skipping, setSkipping] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [results, setResults] = React.useState<SendResult[] | null>(null);
  const [createOpen, setCreateOpen] = React.useState(false);
  const [previewOpen, setPreviewOpen] = React.useState(false);
  /** Empfänger, deren E-Mail frei eingegeben wird (kein Kontakt gewählt). */
  const [customEmail, setCustomEmail] = React.useState<Set<string>>(() => new Set());
  const bodyRef = React.useRef<HTMLTextAreaElement | null>(null);

  const base = `/api/ausschreibungen/${encodeURIComponent(ausschreibung.id)}`;

  React.useEffect(() => {
    let cancelled = false;
    setOrgsLoading(true);
    const t = window.setTimeout(() => {
      const params = new URLSearchParams({ type: 'Lieferant' });
      if (query.trim()) params.set('q', query.trim());
      fetch(`/api/organizations?${params.toString()}`, { credentials: 'include' })
        .then(async (r) => {
          const data = await r.json().catch(() => null);
          if (!r.ok) throw new Error((data as { error?: string } | null)?.error || 'Lieferanten konnten nicht geladen werden.');
          return (Array.isArray(data) ? data : (data as { items?: unknown[] } | null)?.items ?? []) as SupplierOrg[];
        })
        .then((rows) => {
          if (cancelled) return;
          setOrgs(rows);
          setOrgsError(null);
        })
        .catch((e) => {
          if (!cancelled) setOrgsError(e instanceof Error ? e.message : String(e));
        })
        .finally(() => {
          if (!cancelled) setOrgsLoading(false);
        });
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [query]);

  React.useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [dispatch, accountsRes] = await Promise.all([
          fetchDispatchAccount('general').catch(() => null),
          fetch('/api/inbox/accounts', { credentials: 'include' }),
        ]);
        const rows = accountsRes.ok ? ((await accountsRes.json()) as Array<Record<string, unknown>>) : [];
        if (cancelled) return;
        const list = (Array.isArray(rows) ? rows : [])
          .filter((a) => a?.nylas_grant_id || a?.is_demo_simulated_connection)
          .map((a) => ({ id: String(a.id), email: String(a.email ?? a.nylas_email ?? '').trim() }))
          .filter((a) => a.email);
        setMailboxes(list);
        setAccountId(dispatch?.account_id ?? list[0]?.id ?? '');
      } catch {
        if (!cancelled) setMailboxes([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    fetch(`${base}/supplier-requests/attachment?meta=1`, { credentials: 'include' })
      .then(async (r) => {
        const data = (await r.json().catch(() => ({}))) as AttachmentMeta & { error?: string };
        if (cancelled) return;
        if (!r.ok) {
          setAttachmentError(data.error || 'Die GAEB-Datei konnte nicht erstellt werden.');
          return;
        }
        setAttachment(data);
      })
      .catch(() => {
        if (!cancelled) setAttachmentError('Die GAEB-Datei konnte nicht erstellt werden.');
      });
    return () => {
      cancelled = true;
    };
  }, [base]);

  const selectedIds = React.useMemo(() => new Set(recipients.map((r) => r.organizationId)), [recipients]);
  const recipientById = React.useMemo(() => new Map(recipients.map((r) => [r.organizationId, r])), [recipients]);
  /** Suchtreffer; ausgewählte Lieferanten außerhalb der Suche bleiben oben sichtbar. */
  const listedOrgs = React.useMemo(() => {
    const inResults = new Set(orgs.map((o) => o.id));
    const pinned: SupplierOrg[] = recipients
      .filter((r) => !inResults.has(r.organizationId))
      .map((r) => ({ id: r.organizationId, name: r.supplierName, primary_contact: null }));
    return [...pinned, ...orgs];
  }, [orgs, recipients]);

  const loadContacts = React.useCallback(async (orgId: string) => {
    try {
      const r = await fetch(`/api/organizations/${encodeURIComponent(orgId)}/contacts`, { credentials: 'include' });
      const rows = r.ok ? ((await r.json()) as OrgContact[]) : [];
      setRecipients((prev) =>
        prev.map((rec) => {
          if (rec.organizationId !== orgId) return rec;
          const withEmail = rows.filter((c) => c.email?.trim());
          // Hauptkontakt ohne Mail-Adresse: den ersten Kontakt mit Adresse vorschlagen.
          const pick =
            (rec.contactId && rows.find((c) => c.id === rec.contactId && c.email?.trim())) ||
            withEmail.find((c) => c.is_primary) ||
            withEmail[0] ||
            null;
          return {
            ...rec,
            contacts: rows,
            contactId: rec.email ? rec.contactId : pick?.id ?? null,
            name: rec.email ? rec.name : pick?.name?.trim() ?? '',
            email: rec.email || pick?.email?.trim() || '',
          };
        }),
      );
    } catch {
      setRecipients((prev) => prev.map((rec) => (rec.organizationId === orgId ? { ...rec, contacts: [] } : rec)));
    }
  }, []);

  const toggleOrg = (org: SupplierOrg) => {
    if (selectedIds.has(org.id)) {
      setRecipients((prev) => prev.filter((r) => r.organizationId !== org.id));
      setCustomEmail((prev) => {
        const next = new Set(prev);
        next.delete(org.id);
        return next;
      });
      return;
    }
    const pc = org.primary_contact;
    setRecipients((prev) => [
      ...prev,
      {
        organizationId: org.id,
        supplierName: org.name,
        contacts: null,
        contactId: pc?.email?.trim() ? pc.id : null,
        name: pc?.email?.trim() ? pc.name?.trim() ?? '' : '',
        email: pc?.email?.trim() ?? '',
      },
    ]);
    void loadContacts(org.id);
  };

  const updateRecipient = (orgId: string, patch: Partial<Recipient>) =>
    setRecipients((prev) => prev.map((r) => (r.organizationId === orgId ? { ...r, ...patch } : r)));

  const insertPlaceholder = (key: string) => {
    const token = `{{${key}}}`;
    const el = bodyRef.current;
    if (!el) {
      setBody((b) => `${b}${token}`);
      return;
    }
    const start = el.selectionStart ?? body.length;
    const end = el.selectionEnd ?? body.length;
    const next = `${body.slice(0, start)}${token}${body.slice(end)}`;
    setBody(next);
    window.requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const invalid = recipients.filter((r) => !EMAIL_RE.test(r.email.trim()));
  const canSend =
    canEdit &&
    !sending &&
    recipients.length > 0 &&
    invalid.length === 0 &&
    subject.trim().length > 0 &&
    body.trim().length > 0 &&
    Boolean(attachment) &&
    mailboxes.length > 0;

  const send = async (resend = false, list: Recipient[] = recipients) => {
    setSending(true);
    setError(null);
    try {
      const res = await fetch(`${base}/supplier-requests`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          accountId: accountId || null,
          subject,
          body,
          resend,
          recipients: list.map((r) => ({
            organizationId: r.organizationId,
            contactId: r.contactId,
            supplierName: r.supplierName,
            email: r.email.trim(),
            name: r.name.trim(),
          })),
        }),
      });
      const data = (await res.json().catch(() => ({}))) as { results?: SendResult[]; error?: string };
      if (!res.ok || !data.results) throw new Error(data.error || 'Anfrage konnte nicht gesendet werden.');
      setResults(data.results);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSending(false);
    }
  };

  const skip = async () => {
    setSkipping(true);
    setError(null);
    try {
      const res = await fetch(`${base}/supplier-requests/skip`, { method: 'POST', credentials: 'include' });
      const data = (await res.json().catch(() => ({}))) as { supplier_request_state?: 'sent' | 'skipped'; error?: string };
      if (!res.ok) throw new Error(data.error || 'Schritt konnte nicht übersprungen werden.');
      onDone(data.supplier_request_state ?? 'skipped');
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSkipping(false);
    }
  };

  if (results) {
    const sentCount = results.filter((r) => r.status === 'sent').length;
    const failed = results.filter((r) => r.status === 'failed');
    const alreadySent = results.filter((r) => r.status === 'already_sent');
    return (
      <section className="rounded-lg border border-app-border bg-app-surface p-5 shadow-sm space-y-4">
        <h2 className="text-base font-semibold text-app-text">Anfrage verschickt</h2>
        <ul className="divide-y divide-app-border rounded-md border border-app-border">
          {results.map((r) => (
            <li key={`${r.supplier_name}-${r.email}`} className="flex items-start gap-3 px-3 py-2 text-sm">
              {r.status === 'failed' ? (
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />
              ) : (
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
              )}
              <div className="min-w-0 flex-1">
                <div className="font-medium text-app-text">{r.supplier_name || r.email}</div>
                <div className="text-app-text-subtle">
                  {r.email} ·{' '}
                  {r.status === 'sent'
                    ? 'gesendet'
                    : r.status === 'already_sent'
                      ? 'wurde schon angefragt, nicht erneut gesendet'
                      : r.status === 'in_progress'
                        ? 'wird gerade gesendet'
                        : `fehlgeschlagen: ${r.error ?? ''}`}
                </div>
              </div>
            </li>
          ))}
        </ul>
        <div className="flex flex-wrap items-center justify-end gap-2 pt-2 border-t border-app-border">
          {alreadySent.length > 0 && (
            <button
              type="button"
              disabled={sending}
              onClick={() => {
                const again = recipients.filter((p) => alreadySent.some((s) => s.email === p.email.trim()));
                setRecipients(again);
                void send(true, again);
              }}
              className="text-sm text-app-text-muted hover:text-pipedrive-blue px-3 py-2 rounded-md transition disabled:opacity-50"
            >
              {alreadySent.length === 1 ? 'Bereits Angefragten erneut senden' : `${alreadySent.length} bereits Angefragte erneut senden`}
            </button>
          )}
          {failed.length > 0 && (
            <button
              type="button"
              onClick={() => {
                setRecipients((prev) => prev.filter((p) => failed.some((f) => f.email === p.email.trim())));
                setResults(null);
              }}
              className="text-sm text-app-text-muted hover:text-pipedrive-blue px-3 py-2 rounded-md transition"
            >
              Fehlgeschlagene bearbeiten
            </button>
          )}
          <button
            type="button"
            onClick={() => onDone(sentCount > 0 || results.some((r) => r.status === 'already_sent') ? 'sent' : 'skipped')}
            className="inline-flex items-center gap-2 rounded-md bg-pipedrive-blue px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 transition"
          >
            Weiter zur Akte
          </button>
        </div>
      </section>
    );
  }

  const previewRecipient = recipients[0] ?? null;

  return (
    <section className="rounded-lg border border-app-border bg-app-surface p-5 shadow-sm space-y-5">
      <div>
        <h2 className="text-base font-semibold text-app-text">Großhändler anfragen</h2>
        <p className="text-sm text-app-text-subtle mt-1">
          Das LV geht als GAEB X83 ohne Preise an jeden gewählten Großhändler, je Empfänger eine eigene Mail. Die
          Angebote lädst du danach in der Akte hoch. Du kannst den Schritt auch überspringen.
        </p>
      </div>

      {(error || attachmentError) && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 inline-flex items-center gap-2">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {error || attachmentError}
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-2">
        <div className="space-y-3 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-app-text">
              Großhändler{recipients.length > 0 ? ` · ${recipients.length} ausgewählt` : ''}
            </h3>
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm font-medium text-pipedrive-blue hover:bg-app-hover transition"
            >
              <Plus className="h-4 w-4" />
              Neuer Lieferant
            </button>
          </div>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Lieferant suchen…"
              className={`${inputClass} pl-9`}
            />
          </div>
          <div className="app-scrollbar max-h-[28rem] overflow-y-auto rounded-lg border border-app-border">
            {listedOrgs.length === 0 && orgsLoading ? (
              <div className="flex justify-center py-6">
                <LoadingSpinner size="sm" />
              </div>
            ) : orgsError && listedOrgs.length === 0 ? (
              <p className="px-3 py-3 text-sm text-red-700">{orgsError}</p>
            ) : listedOrgs.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-app-text-subtle">
                {query.trim() ? `Kein Lieferant zu „${query.trim()}“ gefunden.` : 'Noch keine Lieferanten in den Kontakten.'}
                <button
                  type="button"
                  onClick={() => setCreateOpen(true)}
                  className="mt-2 flex w-full items-center justify-center gap-1 font-medium text-pipedrive-blue hover:underline"
                >
                  <Plus className="h-4 w-4" />
                  {query.trim() ? `„${query.trim()}“ als Lieferant anlegen` : 'Lieferant anlegen'}
                </button>
              </div>
            ) : (
              <ul className="divide-y divide-app-border">
                {listedOrgs.map((org) => {
                  const r = recipientById.get(org.id) ?? null;
                  const selected = r != null;
                  const emailInvalid = r ? !EMAIL_RE.test(r.email.trim()) : false;
                  const custom = r ? customEmail.has(org.id) || (r.contacts != null && !r.contactId) : false;
                  const contactOptions = r?.contacts
                    ? [
                        ...r.contacts
                          .filter((c) => c.email?.trim())
                          .map((c) => ({
                            value: c.id,
                            label: c.name?.trim() ? `${c.name.trim()} · ${c.email}` : String(c.email),
                          })),
                        { value: CUSTOM_EMAIL, label: 'Andere E-Mail-Adresse…' },
                      ]
                    : [];
                  return (
                    <li key={org.id}>
                      <button
                        type="button"
                        onClick={() => toggleOrg(org)}
                        aria-pressed={selected}
                        className="flex w-full items-center gap-3 px-3 py-2.5 text-left text-sm hover:bg-app-hover transition"
                      >
                        <span
                          className={`flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[5px] border transition ${
                            selected ? 'border-pipedrive-blue bg-pipedrive-blue text-white' : 'border-app-border-strong'
                          }`}
                        >
                          {selected && <Check className="h-3 w-3" strokeWidth={3} />}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate font-medium text-app-text">{org.name}</span>
                          {!selected && (
                            <span className="block truncate text-app-text-subtle">
                              {org.primary_contact?.email || 'Keine E-Mail hinterlegt'}
                            </span>
                          )}
                        </span>
                      </button>
                      {r && (
                        <div className="-mt-2 space-y-2 pb-2.5 pl-[2.625rem] pr-3">
                          {r.contacts === null ? (
                            <p className="inline-flex items-center gap-2 text-sm text-app-text-subtle">
                              <LoadingSpinner size="xs" /> Lade Ansprechpartner…
                            </p>
                          ) : contactOptions.length > 1 ? (
                            <AppSelect
                              size="sm"
                              variant="ghost"
                              className="-ml-2 w-fit max-w-full"
                              triggerClassName="!min-h-0 !px-2 !py-0.5 !text-app-text-subtle !font-normal"
                              value={custom ? CUSTOM_EMAIL : r.contactId ?? CUSTOM_EMAIL}
                              options={contactOptions}
                              aria-label={`Ansprechpartner bei ${r.supplierName}`}
                              onChange={(v) => {
                                if (v === CUSTOM_EMAIL) {
                                  setCustomEmail((prev) => new Set(prev).add(org.id));
                                  updateRecipient(org.id, { contactId: null, name: '', email: '' });
                                  return;
                                }
                                const c = r.contacts?.find((x) => x.id === v) ?? null;
                                setCustomEmail((prev) => {
                                  const next = new Set(prev);
                                  next.delete(org.id);
                                  return next;
                                });
                                updateRecipient(org.id, {
                                  contactId: c?.id ?? null,
                                  name: c?.name?.trim() ?? '',
                                  email: c?.email?.trim() ?? '',
                                });
                              }}
                            />
                          ) : null}
                          {r.contacts !== null && (custom || contactOptions.length <= 1) && (
                            <div className="relative">
                              <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                              <input
                                type="email"
                                value={r.email}
                                onChange={(e) => updateRecipient(org.id, { email: e.target.value })}
                                placeholder="E-Mail-Adresse des Großhändlers"
                                className={`${inputClass} pl-9 ${emailInvalid && r.email.trim() ? '!border-red-400' : ''}`}
                                aria-invalid={emailInvalid}
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        <div className="space-y-3 min-w-0">
          <h3 className="text-sm font-semibold text-app-text">Mail</h3>
          {mailboxes.length === 0 ? (
            <p className="text-sm text-app-text-subtle">Kein verbundenes Postfach. Verbinde eins unter Einstellungen → E-Mail.</p>
          ) : (
            <div className="text-sm">
              <span className="mb-1 block text-xs font-medium text-app-text-muted">Absender</span>
              <AppSelect
                value={accountId}
                onChange={setAccountId}
                options={mailboxes.map((m) => ({ value: m.id, label: m.email }))}
                icon={<Mail className="h-4 w-4 text-app-text-subtle" />}
                aria-label="Absender"
              />
            </div>
          )}
          <div className="text-sm">
            <span className="mb-1 block text-xs font-medium text-app-text-muted">Betreff</span>
            <TemplateTextField value={subject} onChange={setSubject} labels={PLACEHOLDER_LABELS} aria-label="Betreff" />
          </div>
          <div className="text-sm">
            <span className="mb-1 block text-xs font-medium text-app-text-muted">Text</span>
            <TemplateTextField
              ref={bodyRef as React.Ref<HTMLTextAreaElement | HTMLInputElement>}
              multiline
              rows={11}
              value={body}
              onChange={setBody}
              labels={PLACEHOLDER_LABELS}
              aria-label="Text"
            />
          </div>
          <div className="space-y-1.5">
            <p className="text-xs text-app-text-subtle">
              Platzhalter werden je Empfänger ersetzt. Klick fügt an der Cursorposition ein:
            </p>
            <div className="flex flex-wrap gap-1.5">
              {PLACEHOLDERS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => insertPlaceholder(p.key)}
                  className="inline-flex items-center gap-1 rounded-md bg-pipedrive-blue/10 px-2 py-0.5 text-xs font-medium text-pipedrive-blue ring-1 ring-pipedrive-blue/20 hover:bg-pipedrive-blue/15 transition"
                  title={`{{${p.key}}} einfügen`}
                >
                  <Plus className="h-3 w-3" />
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2 rounded-md border border-app-border bg-app-surface-muted/60 px-3 py-2 text-sm">
            <FileCode2 className="h-4 w-4 shrink-0 text-app-text-subtle" />
            {attachment ? (
              <>
                <button
                  type="button"
                  onClick={() => setPreviewOpen(true)}
                  className="group min-w-0 flex-1 text-left"
                  title="Vorschau öffnen"
                >
                  <span className="block truncate font-medium text-app-text group-hover:text-pipedrive-blue">
                    {attachment.filename}
                  </span>
                  <span className="block text-xs text-app-text-subtle">
                    {attachment.source === 'original'
                      ? 'Original-X83 des Auftraggebers'
                      : 'X83 ohne Preise, aus dem LV erzeugt'}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setPreviewOpen(true)}
                  className="inline-flex items-center gap-1 rounded px-2 py-1 text-sm font-medium text-pipedrive-blue hover:bg-app-hover"
                >
                  <Eye className="h-4 w-4" />
                  Vorschau
                </button>
                <a
                  href={`${base}/supplier-requests/attachment`}
                  className="p-1 rounded text-app-text-subtle hover:bg-app-hover hover:text-pipedrive-blue"
                  title="Anhang herunterladen"
                  aria-label="Anhang herunterladen"
                >
                  <Download className="h-4 w-4" />
                </a>
              </>
            ) : attachmentError ? (
              <span className="text-app-text-subtle">Kein Anhang</span>
            ) : (
              <span className="inline-flex items-center gap-2 text-app-text-subtle">
                <LoadingSpinner size="xs" /> Erstelle GAEB-Datei…
              </span>
            )}
          </div>

          <button
            type="button"
            onClick={() => setShowPreview((v) => !v)}
            className="text-sm text-pipedrive-blue hover:underline"
          >
            {showPreview ? 'Vorschau ausblenden' : 'Vorschau anzeigen'}
          </button>
          {showPreview && (
            <div className="rounded-md border border-app-border bg-app-surface-muted/40 px-3 py-2 text-sm">
              <div className="font-medium text-app-text">{previewText(subject, ausschreibung, previewRecipient)}</div>
              <pre className="mt-2 whitespace-pre-wrap font-sans text-app-text-muted">
                {previewText(body, ausschreibung, previewRecipient)}
              </pre>
              {!previewRecipient && (
                <p className="mt-2 text-xs text-app-text-subtle">Lieferant und Ansprechpartner füllen sich je Empfänger.</p>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-end gap-2 pt-2 border-t border-app-border">
        {invalid.length > 0 && (
          <span className="mr-auto text-sm text-red-700">
            {invalid.length === 1 ? '1 Empfänger ohne gültige E-Mail' : `${invalid.length} Empfänger ohne gültige E-Mail`}
          </span>
        )}
        <button
          type="button"
          onClick={() => void skip()}
          disabled={skipping || sending}
          className="text-sm text-app-text-muted hover:text-pipedrive-blue px-3 py-2 rounded-md transition disabled:opacity-50"
        >
          {skipping ? 'Überspringe…' : 'Überspringen'}
        </button>
        <button
          type="button"
          onClick={() => void send()}
          disabled={!canSend}
          className="inline-flex items-center gap-2 rounded-md bg-pipedrive-blue px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
          title={!canEdit ? 'Recht „Ausschreibungen bearbeiten“ nötig' : undefined}
        >
          {sending ? <LoadingSpinner size="xs" /> : null}
          {recipients.length === 1
            ? 'Anfrage an 1 Großhändler senden'
            : `Anfrage an ${recipients.length} Großhändler senden`}
        </button>
      </div>

      <TenderGaebPreviewModal open={previewOpen} onClose={() => setPreviewOpen(false)} base={base} />

      {createOpen && (
        <CreateOrganizationModal
          variant="drawer"
          initialType="Lieferant"
          initialName={query.trim()}
          onClose={() => setCreateOpen(false)}
          onCreated={(org) => {
            setCreateOpen(false);
            const created: SupplierOrg = { id: String(org.id), name: org.name, primary_contact: null };
            setOrgs((prev) => (prev.some((o) => o.id === created.id) ? prev : [created, ...prev]));
            // Neu angelegt heißt: soll angefragt werden.
            if (!selectedIds.has(created.id)) toggleOrg(created);
          }}
        />
      )}
    </section>
  );
};
